import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getRoomById,
  createRoom,
  updateRoom,
  deleteRoom,
  type RoomFilters,
  type Room,
  getRooms,
  createBulkRoom,
} from '../../services';

// Query keys factory
export const roomKeys = {
  all: ['rooms'] as const,
  lists: () => [...roomKeys.all, 'list'] as const,
  list: (filters: RoomFilters) =>
    [...roomKeys.lists(), { ...filters }] as const,
  details: () => [...roomKeys.all, 'detail'] as const,
  detail: (id: number) => [...roomKeys.details(), id] as const,
};

// List query with filters and pagination
export function useRooms(filters: RoomFilters) {
  return useQuery({
    queryKey: roomKeys.list(filters),
    queryFn: () => getRooms(filters),
    placeholderData: (previousData) => previousData,
  });
}

// Single room query
export function useRoom(id: number) {
  return useQuery({
    queryKey: roomKeys.detail(id),
    queryFn: () => getRoomById(id),
    enabled: !!id,
  });
}

// Create mutation
export function useCreateRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createRoom,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roomKeys.lists() });
    },
  });
}

// Update mutation
export function useUpdateRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Room> }) =>
      updateRoom(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: roomKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roomKeys.detail(id) });
    },
  });
}

// Delete mutation
export function useDeleteRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteRoom,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roomKeys.lists() });
    },
  });
}

// Bulk Create mutation
export function useCreateBulkRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createBulkRoom,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roomKeys.lists() });
    },
  });
}
